import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("ani-admin-token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-[100svh] items-center justify-center bg-[#09121F] px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-xl border border-amber-500/25 bg-[#0A1622] p-8 shadow-2xl sm:p-10"
      >
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo-mark.png" alt="Al Nama Importer" className="h-16 w-auto object-contain" />
          <h1 className="mt-5 font-serif text-2xl font-semibold text-emerald-50">Admin Login</h1>
          <p className="mt-1 text-xs uppercase tracking-[0.25em] text-slate-500">Enquiries Dashboard</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-slate-400 font-display">Email</label>
            <input
              data-testid="admin-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-amber-500/20 bg-[#0B1D16]/60 px-4 py-3 text-sm text-emerald-50 outline-none transition-colors focus:border-amber-500/60"
              placeholder="admin@alnamaimporter.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-[0.18em] text-slate-400 font-display">Password</label>
            <input
              data-testid="admin-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-amber-500/20 bg-[#0B1D16]/60 px-4 py-3 text-sm text-emerald-50 outline-none transition-colors focus:border-amber-500/60"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-6 py-3.5 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0B1D16] transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Sign In
          </button>
        </form>

        <p className="mt-6 text-center">
          <Link to="/" className="text-xs text-slate-500 transition-colors hover:text-amber-400">
            ← Back to website
          </Link>
        </p>
      </motion.div>
    </main>
  );
}
